<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { SidebarMenuItem, MenuPermission, MenuClickEvent } from '@/types/sidebar-menu.types'
import JDynamicMenuItem from './JSidebarSimple/JDynamicMenuItem.vue'
import { cn } from '@/lib/utils'
import { filterMenuItems, getExpandedKeysForSearch } from '@/lib/menu-utils'

/**
 * JTree - 트리 뷰 컴포넌트
 * Tree View Component
 *
 * @description
 * 계층 데이터를 조회/탐색하는 읽기 전용 트리 컴포넌트입니다.
 * JDynamicMenuItem을 재귀적으로 렌더링하며, 네비게이션 기능을 비활성화하고
 * nodeClick 이벤트로 선택을 처리합니다.
 *
 * @example
 * ```vue
 * <JTree
 *   :items="treeData"
 *   v-model:expanded-keys="expandedKeys"
 *   v-model:active-key="activeKey"
 *   @node-click="handleNodeClick"
 * />
 * ```
 *
 * @example JSON 트리 데이터 예시
 * ```json
 * [
 *   {
 *     "label": "프로그램 관리",
 *     "icon": "folder",
 *     "menuType": "F",
 *     "menuKey": 1,
 *     "children": [
 *       {
 *         "label": "시스템 관리",
 *         "menuType": "L",
 *         "menuKey": 11
 *       },
 *       {
 *         "label": "사용자 관리",
 *         "menuType": "L",
 *         "menuKey": 12
 *       }
 *     ]
 *   }
 * ]
 * ```
 */

type StyleType = 'default' | 'minimal'

const props = withDefaults(
  defineProps<{
    /** 트리 노드 데이터 */
    items: SidebarMenuItem[]
    /** 펼쳐진 노드 키 목록 (v-model 지원, 배열) */
    expandedKeys?: (number | string)[]
    /** 현재 선택(하이라이트)된 노드의 menuKey (v-model 지원) */
    activeKey?: number | string | null
    /** 권한 목록 */
    permissions?: MenuPermission[]
    /** 최대 깊이 제한 (무한 루프 방지) */
    maxDepth?: number
    /** 스타일 타입 */
    styletype?: StyleType
    /** 검색어 (노드 필터링) */
    searchQuery?: string
    /** 추가 CSS 클래스 */
    class?: string
  }>(),
  {
    expandedKeys: () => [],
    activeKey: null,
    permissions: () => [],
    maxDepth: 10,
    styletype: 'minimal',
    searchQuery: '',
  },
)

const emit = defineEmits<{
  /** 노드 클릭 이벤트 */
  nodeClick: [event: MenuClickEvent]
  /** 확장 상태 변경 이벤트 */
  expandChange: [menuKey: number | string | undefined, expanded: boolean]
  /** expandedKeys v-model 업데이트 */
  'update:expandedKeys': [keys: (number | string)[]]
  /** activeKey v-model 업데이트 */
  'update:activeKey': [key: number | string | null]
}>()

/**
 * 내부 확장 키 관리 (Set으로 변환하여 사용)
 */
const internalExpandedKeys = ref<Set<number | string>>(new Set(props.expandedKeys))

/**
 * 검색어로 필터링된 트리 아이템
 */
const filteredTreeItems = computed(() => {
  return filterMenuItems(props.items, props.searchQuery || '')
})

/**
 * 검색 결과에 따라 부모 노드 자동 확장
 */
watch(
  () => filteredTreeItems.value,
  (filtered) => {
    if (!props.searchQuery || props.searchQuery.trim() === '') {
      return
    }

    // 검색 결과에서 매칭된 하위 노드가 있는 부모를 찾아 확장
    const keysToExpand = getExpandedKeysForSearch(filtered)
    keysToExpand.forEach(key => {
      internalExpandedKeys.value.add(key)
    })

    // v-model 업데이트
    emit('update:expandedKeys', [...internalExpandedKeys.value])
  },
  { immediate: false }
)

/**
 * expandedKeys prop 변경 감지
 */
watch(
  () => props.expandedKeys,
  (newKeys) => {
    internalExpandedKeys.value = new Set(newKeys)
  },
  { deep: true }
)

/**
 * 확장 상태 변경 핸들러
 */
const handleExpandChange = (menuKey: number | string | undefined, expanded: boolean) => {
  if (!menuKey) return

  // 새로운 Set 생성 (reactivity 보장)
  const newKeys = new Set(internalExpandedKeys.value)

  if (expanded) {
    newKeys.add(menuKey)
  } else {
    newKeys.delete(menuKey)
  }

  internalExpandedKeys.value = newKeys

  // v-model 업데이트 및 이벤트 발생
  emit('update:expandedKeys', [...newKeys])
  emit('expandChange', menuKey, expanded)
}

/**
 * 노드 클릭 핸들러
 */
const handleNodeClick = (event: MenuClickEvent) => {
  // activeKey 업데이트
  const menuKey = event.menuItem.menuKey
  if (menuKey !== undefined) {
    emit('update:activeKey', menuKey)
  }

  // nodeClick 이벤트 발생
  emit('nodeClick', event)
}

/**
 * 스타일 프리셋
 */
const STYLE_PRESETS: Record<StyleType, {
  containerClass: string
  menuPaddingClass: string
}> = {
  default: {
    containerClass: 'w-full',
    menuPaddingClass: 'space-y-1',
  },
  minimal: {
    containerClass: 'w-full',
    menuPaddingClass: 'space-y-0.5',
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

/**
 * 루트 클래스
 */
const rootClasses = computed(() => {
  return cn(
    preset.value.containerClass,
    props.class
  )
})
</script>

<template>
  <div :class="rootClasses">
    <div :class="preset.menuPaddingClass">
      <JDynamicMenuItem
        v-for="(item, index) in filteredTreeItems"
        :key="item.menuKey || item.label || index"
        :item="item"
        :level="0"
        :max-depth="maxDepth"
        :permissions="permissions"
        :expanded-keys="internalExpandedKeys"
        :styletype="styletype"
        :disable-navigation="true"
        :active-key="activeKey"
        @menu-click="handleNodeClick"
        @expand-change="handleExpandChange"
      />
    </div>
  </div>
</template>
